import React, { FC, useState, useEffect, ChangeEvent, useRef, useId } from 'react';
import axiosInstance from 'config/axiosInstance';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/solid';
import { DriverCandidate, driverCandidatePage } from 'components/Driver/driverCandidates';
import user_icon from '../../../assets/icons/user.svg';
import add_driver_icon from '../../../assets/icons/add-driver.svg';
import { useTranslation } from 'react-i18next';
import { useClassifiedErrorMessages } from 'reusable/useSaveError';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';
import SearchCombobox from 'reusable/RemoteSearch/SearchCombobox';
import { ClientCandidate, clientCandidatePage } from 'components/Client/Utility/candidates';
import { useRemoteSearch } from 'reusable/RemoteSearch/useRemoteSearch';
import RemoteListFooter from 'reusable/RemoteSearch/RemoteListFooter';

type DriverInterface = DriverCandidate;

interface AssignDriverDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AssignDriverDialog: FC<AssignDriverDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('client');
  // EV-338（2026-10-14 PM 判断）。読み上げの名前は、名前が空なら「名前のない運転者」（見える文字は EV-314 の「-」）。
  // 以前は空のまま渡し、「 を追加」のように読まれた。
  const spokenName = (name?: string | null) => name || t('ui.unnamed-driver', { ns: 'common' });
  // EV-493。配送先は全件（最大5,000件・50回）を読まず、名前で検索する欄（SearchCombobox）で選ぶ。選んだ配送先は
  // 行そのもの（今の割当 assign_drivers を含む）を持つ。以前の配送先の欄（EV-198・ADM-16-AC2 の combobox）と同じ
  // キー操作は SearchCombobox が持つ。
  const [selectedClient, setSelectedClient] = useState<ClientCandidate | null>(null);
  const [assignedDrivers, setAssignedDrivers] = useState<DriverInterface[]>([]);
  const [assignedDriversIds, setAssignedDriversIds] = useState<number[]>([]);
  // 割り当てに失敗した理由。以前はコンソールに出すだけだった。
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const errorMessages = useClassifiedErrorMessages();

  const driverSearchRef = useRef<HTMLInputElement>(null);
  const assignedListRef = useRef<HTMLDivElement>(null);
  // 外した運転者の位置。描画の後に、その位置の外すボタン（無ければ運転者の検索）へフォーカスを移す。
  const pendingFocus = useRef<number | null>(null);
  const clientInputId = useId();
  const clientLabelId = useId();
  // EV-493。運転者の候補はサーバーで名前を検索し、20件ずつ読む（以前は全件を読み、画面で名前を絞っていた）。
  // A10。運転者は複数の配送先に割り当てられる。選んだ配送先にもう割り当てている運転者だけを「割り当て済み」にする
  // （以前はどこかの配送先に割り当てていれば「割り当て済み」で選べなかった）。
  const driverSearch = useRemoteSearch<DriverInterface>({
    fetchPage: driverCandidatePage,
    getKey: (driver) => driver.id,
    enabled: isOpen,
  });
  const filteredDrivers = driverSearch.items;
  const driverSearchTerm = driverSearch.query;

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    driverSearch.setQuery(e.target.value);
  };

  /** EV-195。外したら、次の外すボタン（無ければ前、それも無ければ運転者の検索）へフォーカスを移す。 */
  const removeAssignedDriver = (driver: DriverInterface, index: number) => {
    pendingFocus.current = index;
    handleDriverSelection(driver);
  };

  useEffect(() => {
    const index = pendingFocus.current;
    if (index === null) return;
    pendingFocus.current = null;
    const buttons = Array.from(
      assignedListRef.current?.querySelectorAll<HTMLButtonElement>('[data-remove-driver]') ?? []
    );
    const next = buttons[Math.min(index, buttons.length - 1)];
    (next ?? driverSearchRef.current)?.focus();
  }, [assignedDrivers]);

  const handleDriverSelection = (driver: DriverInterface) => {
    if (assignedDrivers.find((d) => d.id === driver.id)) {
      setAssignedDrivers(assignedDrivers.filter((d) => d.id !== driver.id));
      setAssignedDriversIds(
        assignedDriversIds.filter((id) => id !== driver.id)
      );
    } else {
      setAssignedDrivers([...assignedDrivers, driver]);
      setAssignedDriversIds([...assignedDriversIds, driver.id]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // EV-493。選んだ配送先は検索の候補の行そのもの（前回に開いたときの選択は閉じたときに消す。PR #253 の P2）。
      if (selectedClient) {
        const currentDrivers = selectedClient.assign_drivers || [];
        const updatedDrivers = Array.from(
          new Set([...currentDrivers, ...assignedDriversIds])
        );

        await axiosInstance.patch(
          `/v1/client_deliveries/${selectedClient.id}/`,
          {
            assign_drivers: updatedDrivers,
          }
        );

        onClose();
        window.location.reload();
      } else {
        setSaveErrors([t('ui.select-client')]);
      }
    } catch (error) {
      console.error('Error assigning client:', error);
      setSaveErrors(errorMessages(error));
    }
  };

  const resetData = () => {
    setSelectedClient(null);
    driverSearch.setQuery('');
    setAssignedDrivers([]);
    // ADM-13。送信用のIDを消し忘れると、次に開いたときに前回の選択が
    // そのまま送られ、意図しない運転者が割り当てられる。
    setAssignedDriversIds([]);
    // RV-028。前回の失敗の説明も消す。消し忘れると、次に開いたときにも出ていた。
    setSaveErrors([]);
  };

  useEffect(() => {
    if (!isOpen) {
      resetData();
    }
  }, [isOpen]);

  // ADM-16。Escape で閉じる・フォーカスを中に留める・閉じたら開いた操作へ戻す。
  // RV-125。見出しの id は文書の中で一意にする（重なったダイアログが別の見出しで読まれた）。
  const headingId = useId();
  const dialogRef = useModalDialog(isOpen, onClose);

  if (!isOpen) {
    return null;
  }

  return inModalLayer(
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div
          className="inline-block align-middle bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 w-[768px]"
          role="dialog"
          ref={dialogRef}
          aria-modal="true"
          aria-labelledby={headingId}
          tabIndex={-1}
        >
          <div className="bg-white">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-left sm:mt-0 w-full">
                <div className="flex justify-between items-center mt-4 mx-6">
                  <h3
                    className="text-subtitle-1 font-medium text-neutral/500"
                    id={headingId}
                  >
                    {t('manage_client.assign_driver_title')}
                  </h3>
                  <button type="button"
                    onClick={onClose}
                    className="text-gray-600 hover:text-gray-800 focus:outline-none"
                  >
                    <span className="sr-only">{t('ui.close', { ns: 'common' })}</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <hr className="border-neutral/50 mt-4 w-full" />
                <form onSubmit={handleSubmit}>
                  <div className="px-6 pt-6">
                    <label
                      id={clientLabelId}
                      htmlFor={clientInputId}
                      className="block text-caption font-normal text-neutral/100"
                    >
                      {t('manage_client.client')}
                    </label>
                    {/* ADM-16-AC2・EV-198。WAI-ARIA の combobox（一覧の候補・自動で選ばない）。EV-493。候補はサーバーで
                        名前を検索して20件ずつ読む（以前は配送先を全件読んで画面で絞っていた）。読めなかったときの
                        表示（EV-318 の既存の文言・失敗の種類・再読み込み）は欄の下に出す。 */}
                    <div className="relative mt-1">
                      <SearchCombobox<ClientCandidate>
                        id={clientInputId}
                        label={t('manage_client.client')}
                        placeholder={t('manage_client.select_client')}
                        selected={selectedClient ? [selectedClient] : []}
                        fetchPage={clientCandidatePage}
                        getKey={(client) => client.id}
                        getLabel={(client) => client.name}
                        onSelect={(client) => setSelectedClient(client)}
                        boxClassName="pr-10"
                      />
                      <ChevronDownIcon
                        className="w-5 h-5 absolute top-3 right-4 text-neutral/75 pointer-events-none"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-6 px-6 pt-4 pb-6">
                    <div className="w-full">
                      <div className="relative">
                        <input
                          ref={driverSearchRef}
                          type="text"
                          aria-label={t('manage_client.search_driver')}
                          placeholder={t('manage_client.search_driver')}
                          className="block w-full h-10 pl-4 text-caption border border-neutral/50 focus:outline-none focus:ring-neutral/75 focus:border-neutral/75 sm:text-sm rounded-md"
                          value={driverSearchTerm}
                          onChange={handleSearchChange}
                        />
                        <MagnifyingGlassIcon
                          className="w-4 h-4 absolute inset-y-3 right-4 text-neutral/75 pointer-events-none"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="h-80 mt-4 overflow-y-scroll pr-4">
                        {filteredDrivers.map((driver) => (
                          <div
                            key={driver.id}
                            className="flex items-center justify-between py-2 border-b"
                          >
                            <div className="flex items-center">
                              <img
                                src={driver.profile_picture || user_icon}
                                alt=""
                                className="h-10 w-10 object-cover rounded-full"
                              />
                              <div className="ml-4">
                                {/* EV-314。名前・電話番号の空は「-」（EV-311 の表の空と同じ）。 */}
                                <p className="text-body-2 font-medium text-neutral/300">
                                  {driver.name || '-'}
                                </p>
                                <p className="text-caption text-neutral/100">
                                  {driver.phone || '-'}
                                </p>
                              </div>
                            </div>
                            {!(selectedClient?.assign_drivers ?? []).includes(driver.id) ? (
                              // EV-195。追加の前後で同じ button を使い、押した後もフォーカスを失わない。
                              // 追加済みは aria-disabled（押しても何もしない。外すのは右の一覧から）。
                              assignedDrivers.some((d) => d.id === driver.id) ? (
                                <button
                                  type="button"
                                  className="w-8 h-8 flex items-center justify-center"
                                  aria-label={t('manage_client.driver_added', { name: spokenName(driver.name) })}
                                  aria-disabled="true"
                                  onClick={() => {}}
                                >
                                  <CheckCircleIcon className="w-6 h-6 text-success" aria-hidden="true" />
                                </button>
                              ) : (
                                <button
                                  className="w-8 h-8 bg-primary/50 rounded-md flex items-center justify-center"
                                  aria-label={t('manage_client.add_driver', { name: spokenName(driver.name) })}
                                  onClick={() => handleDriverSelection(driver)}
                                  type="button"
                                >
                                  <PlusIcon className="w-4 h-4 text-primary/300" aria-hidden="true" />
                                </button>
                              )
                            ) : (
                              <span className="text-caption text-neutral/100">
                                {t('manage_client.unavailable')}
                              </span>
                            )}
                          </div>
                        ))}
                        <RemoteListFooter search={driverSearch} />
                      </div>
                    </div>
                    {assignedDrivers.length > 0 ? (
                      <div className="text-left w-full">
                        {/* EV-006。人数は翻訳の表記で出す（英語の driver を固定で付けていた）。 */}
                        <h6 className="text-subtitle-2 font-medium">
                          {t('ui.total-assignment-count', { count: assignedDrivers.length })}
                        </h6>
                        <div ref={assignedListRef} className="mt-4 flex flex-wrap gap-2 overflow-y-scroll">
                          {assignedDrivers.map((driver, index) => (
                            <div
                              key={driver.id}
                              className="flex items-center gap-2 h-14 px-3 rounded-md border border-neutral/50"
                            >
                              <img
                                src={driver.profile_picture || user_icon}
                                alt=""
                                className="h-10 w-10 object-cover rounded-full"
                              />
                              <span className="text-body-2 text-neutral/300">
                                {driver.name || '-'}
                              </span>
                              {/* EV-195。以前は svg の onClick で、キーボードで押せず名前も無かった。 */}
                              <button
                                type="button"
                                data-remove-driver
                                className="text-primary/300"
                                aria-label={t('manage_client.remove_driver', { name: spokenName(driver.name) })}
                                onClick={() => removeAssignedDriver(driver, index)}
                              >
                                <XMarkIcon className="w-6 h-6" aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full text-center flex flex-col gap-3 justify-center">
                        <img
                          src={add_driver_icon}
                          alt=""
                          className="h-14"
                        />
                        <h6 className="text-subtitle-2 font-medium">
                          {t('manage_client.assign_driver_title')}
                        </h6>
                        <p className="text-caption text-neutral/100">
                          {t('manage_client.assign_driver_subtitle_1')}
                          <br /> {t('manage_client.assign_driver_subtitle_2')}
                          <br /> {t('manage_client.assign_driver_subtitle_3')}
                        </p>
                      </div>
                    )}
                  </div>
                  {saveErrors.length > 0 && (
                    <div className="px-4 pb-2 text-error text-body-2" role="alert">
                      {saveErrors.map((message) => (
                        <p key={message}>{message}</p>
                      ))}
                    </div>
                  )}
                  <div className="px-4 py-3 border-t-[1px] grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      className="w-full inline-flex justify-center items-center rounded-md border bg-primary/50 text-subtitle-2 text-primary/300"
                      onClick={onClose}
                    >
                      {t('action.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center items-center rounded-md bg-primary/300 text-subtitle-2 text-white h-12 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('action.assign')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignDriverDialog;
